import { throttle } from 'lodash';
import NextApp, { type AppContext as NextAppContextProps } from 'next/app';
import React, { createRef } from 'react';
import { type Store } from 'react-redux';
import URI from 'urijs';
import Cookies from 'js-cookie';

import type {
  AppContext,
  PageContext,
  GetInitialPropsContext,
  RouterLocation,
} from 'Src/types/common/common';
import type { CMSContent } from 'Src/types/common/cms';
import type { ReduxState } from 'Src/types/redux/redux';
import type { NextAppContext } from 'Src/types/next-serverside';
import type { CMNextPageComponent } from 'Src/types/server-helpers';

import { b2cPagesService, constants, httpHeaders } from '@clubmed/utils';

import config from 'Config';

import { AppProviders } from 'App/AppProviders';
import { RouteChangeListener } from 'App/RouteChangeListener';

import { setInLocalStorage } from 'Client/local-storage-api';
import { createRouterLocation } from 'Client/router/createRouterLocation';

import { ToastContainer } from 'Components/frameworks/toast/Toast.container';
import { HeadInfos } from 'Components/layouts/head-info/HeadInfos';
import { Layout } from 'Components/layouts/layout/Layout';
import { fetchLayoutConfig } from 'Components/layouts/layout/Layout.query';
import { PriorityThirdPartyScripts } from 'Components/third-party/PriorityThirdPartyScripts';
import { ThirdPartyScripts } from 'Components/third-party/ThirdPartyScripts';
import { COOKIE_WARNING_LOCALSTORAGE_KEY } from 'Components/widgets/cookie-warning/constants';
import { Loading } from 'Components/widgets/loading/Loading';
import { InitializeSellerInfos } from 'Components/widgets/seller/InitializeSellerInfos';
import { ThreeSixtyContainer } from 'Components/widgets/three-sixty/ThreeSixtyContainer';

import { getAppContext, saveAppContext } from 'Context/createOrUpdateAppContext';
import { ErrorBoundary } from 'Context/ErrorBoundary';

import { start as handleErrors } from 'Errors/clientErrorHandler';

import {
  createAppContextFromProps,
  createAppContextFromReq,
} from 'Server/context/createAppContextFromReq';
import { patchAppContextUrl } from 'Server/context/patchAppContext';

import { addTranslationKeys, clearAll, getAll, setLocale as setI18nLocale } from 'Src/i18n';

import { SYNC_WITH_LOCATION } from 'Store/constants';
import {
  getInitialReduxState,
  LOCAL_STORAGE_STATE_KEY,
  stateFromCookies,
} from 'Store/getInitialReduxState.client';
import { getKids } from 'Store/trip-information/storeHelper';
import { userInfoSelector } from 'Store/user-info/userInfo.selectors';
import { getOrCreateStore } from 'Store/withReduxStore';

import { getSiteSection } from 'Tracking/utils';

import { BOOKING_PAYMENT_STATUS_OPTION } from 'Utils/booking-payment-status/bookingPaymentStatus.constants';
import { getCurrentUri, isStrictlyDevelopmentEnv } from 'Utils/context/contextUtils';
import { invariant } from 'Utils/invariant/invariant';
import { handleInvalidToken } from 'Utils/ple-utils/handleInvalidToken';
import { getName } from 'Utils/request/requestDictionary';
import {
  STATUS_CODE_200,
  STATUS_CODE_404,
  STATUS_CODE_500,
} from 'Utils/status-code/statusCode.constants';
import { getPageCanonicalUrl } from 'Utils/uri/uriUtils';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';

import { notFoundInitialProps } from 'Pages/_error';
import NotFoundView from 'Views/errors/not-found/NotFoundView';

import 'Components/stylesheets/critical.css';
import 'Components/stylesheets/app.css';

type Props = NextAppContextProps & {
  appContext: AppContext;
  pageContext: PageContext;
  i18nData: CMSContent;
  layoutData: {
    config: CMSContent;
    legalInformations: Array<{
      id: number;
      description: string;
      required: boolean;
    }>;
    newsletters: Array<{
      id: string;
      description: string;
      required: boolean;
    }>;
  };
  initialReduxState: ReduxState;
  statusCode: number;
  disableHeader?: boolean;
  disableFooter?: boolean;
  location?: RouterLocation;
  pageProps?: Record<string, unknown> & {
    pageContext?: Partial<PageContext>;
    statusCode?: number;
  };
};

// PageLayout accepts React children (Layout memo types children as DOM Node).
type PageLayoutProps = Omit<React.ComponentProps<typeof Layout>, 'children'> & {
  disableHeader: boolean;
  disableFooter: boolean;
  isMicePage: boolean;
  children?: React.ReactNode;
};
const PageLayout = Layout as unknown as React.FC<PageLayoutProps>;

class App extends NextApp<Props> {
  static async getInitialProps(nextAppContext: NextAppContext) {
    const { Component, ctx } = nextAppContext;
    const PageComponent = Component as CMNextPageComponent;
    const { pageId, pageType = pageId || 'none', requireAuthentication } = PageComponent;
    const { req, res, asPath, query } = ctx;
    let appContext;

    if (req) {
      appContext = { ...(await createAppContextFromReq(req)), ...patchAppContextUrl(ctx) };
    } else {
      appContext = getAppContext();
    }

    // If no appContext, we reload the page. It's occurring when navigating back from an error layer
    if (!appContext && typeof window !== 'undefined' && isStrictlyDevelopmentEnv(config)) {
      window.location.reload();
    }

    const { isUserQuickLogged, originalUrl, userAgent, device } = appContext;

    // Get or Create the store with initialState from cookies
    // This allows you to set a custom default initialState
    const reduxStore = getOrCreateStore(stateFromCookies(req?.cookies));

    if (requireAuthentication) {
      const currentUri = req ? getCurrentUri(req) : new URI(window.location.href);
      handleInvalidToken({
        appContext,
        currentUri,
        res,
        userInfo: userInfoSelector(reduxStore.getState()) as unknown as UserInfo,
      });
    }

    const location = createRouterLocation({
      asPath,
      query,
      originalUrl,
    });

    const isOption = query.bookingStatusBeforePayment === BOOKING_PAYMENT_STATUS_OPTION;

    // There are two possibilities for the pageContext
    // - it's created in the getInitialProps of the page (DynamicPage.jsx)
    // - it's static, the properties depending on the pages can be statically resolved (e.g. pageId)
    // We create it before the getInitialProps and pass it to the getInitialProps because some
    // components might need it, like ThemeView which does render lines
    const staticPageContext: PageContext = {
      pageId,
      pageTitle: constants.WEBSITE_NAME,
      pageDescription: '',
      // usually we don't care about the statusCode in the pageContext, since a 404/500 will
      // directly route us to a specific branch
      statusCode: STATUS_CODE_200,
      canonicalUrl: getPageCanonicalUrl(originalUrl, originalUrl),
      pageMeta: {},
      pageType,
      siteSection: getSiteSection({ isOption, isUserQuickLogged, pageType }),
    };

    // clear cms keys before handling page request to avoid getting unwanted data
    if (req) {
      clearAll();
    }

    let pageProps: Record<string, any> = {};

    const inheritedAppProps = await NextApp.getInitialProps(nextAppContext);
    pageProps = (inheritedAppProps.pageProps ?? {}) as NonNullable<Props['pageProps']>;

    if (PageComponent.getCMInitialProps) {
      try {
        const initProps = {
          ...ctx,
          reduxStore,
          appContext,
          pageContext: staticPageContext,
          location,
        };

        const CMProps = await PageComponent.getCMInitialProps(initProps);

        pageProps = {
          ...pageProps,
          ...CMProps,
        };
      } catch (err) {
        pageProps = {
          ...pageProps,
          ...err,
        };
      }

      if (pageProps.statusCode === STATUS_CODE_404) {
        try {
          if (typeof window !== 'undefined') {
            throw new Error('NotFound');
          }

          const { getRedirect, RULES } = require('@clubmed/redirects');

          const { status, url } = await getRedirect(config.get(), req, [
            // Those rules only apply to b2c pages that couldn't be found in the CMS content b2c-pages
            RULES.NORMALIZE_B2C_SHOPPING_PATHS, // this redirection rule is temporary (i.e. issue #11090)
            RULES.B2C_CMS_CONTENT,
            RULES.PRODUCT_SHORT_LINKS,
            RULES.GLOBAL_B2C,
            RULES.LEGACY_B2C_NOT_FOUND,
          ]);

          return res.redirect(status, url);
        } catch {
          pageProps = {
            ...pageProps,
            ...(await notFoundInitialProps(req)),
          };
        }
      }
    }

    const {
      pageContext = {
        ...staticPageContext,
        statusCode: pageProps.statusCode || STATUS_CODE_200 || STATUS_CODE_404,
      },
    } = pageProps;

    invariant(
      pageId,
      `The page corresponding to the url ${asPath} doesn't have a pageId. This is an error.`,
    );

    const layoutData = req ? await fetchLayoutConfig(appContext) : {};

    const appContextLight: Partial<AppContext> = {
      locale: appContext.locale,
      userAgent,
      device,
      featureFlippingKeysFromCms: appContext.featureFlippingKeysFromCms,
      originalUrl,
      cookies: {},
    };

    if (typeof window === 'undefined') {
      // Sync store with url, works fine on SSR to have
      // last version of state before rendering on frontend.
      reduxStore.dispatch({
        type: SYNC_WITH_LOCATION,
        payload: {
          location,
          pageType: staticPageContext.pageType,
        },
      });

      if (req.cookies[COOKIE_WARNING_LOCALSTORAGE_KEY]) {
        appContextLight.cookies[COOKIE_WARNING_LOCALSTORAGE_KEY] =
          req.cookies[COOKIE_WARNING_LOCALSTORAGE_KEY];
      }

      res.status(pageContext.statusCode);

      // We set this header to trace the type of request in the logs
      if (!res.headersSent) {
        res.set(
          httpHeaders.X_REQUEST_NAME,
          getName(['PAGE', (pageProps.pageId || staticPageContext.pageType || '').toUpperCase()]),
        );
      }
    }

    return {
      appContext: appContextLight,
      pageContext,
      pageProps,
      i18nData: getAll(),
      layoutData,
      disableHeader:
        pageContext.statusCode === STATUS_CODE_500 ||
        appContext.isSeller ||
        query.disableLayout === 'true' ||
        appContext.isComingFromMobileApp ||
        (typeof window !== 'undefined' &&
          (!!Cookies.get('fromMobileApp') || !!appContext.isComingFromMobileApp)) ||
        (typeof PageComponent.disableHeader === 'function'
          ? PageComponent.disableHeader(appContext)
          : PageComponent.disableHeader),
      disableFooter:
        pageContext.statusCode === STATUS_CODE_500 ||
        appContext.isSeller ||
        isFeatureFlipped(appContext)('isGsa') ||
        query.disableLayout === 'true' ||
        appContext.isComingFromMobileApp ||
        (typeof window !== 'undefined' &&
          (!!Cookies.get('fromMobileApp') || !!appContext.isComingFromMobileApp)) ||
        (typeof PageComponent.disableFooter === 'function'
          ? PageComponent.disableFooter(appContext)
          : PageComponent.disableFooter),
      location,
      initialReduxState: reduxStore.getState(),
      statusCode: pageContext.statusCode,
    };
  }

  appContext: AppContext;
  pageContext: PageContext;
  reduxStore: Store<ReduxState>;
  layoutData: Props['layoutData'];

  toastRef: { current: HTMLElement | null | undefined };

  constructor(props: Props) {
    super(props);

    this.toastRef = createRef();

    this.appContext =
      typeof window !== 'undefined'
        ? saveAppContext(props.appContext)
        : createAppContextFromProps(props.appContext);

    // Get or Create the store with initialState from cookies
    this.reduxStore = getOrCreateStore(
      typeof window !== 'undefined'
        ? getInitialReduxState(props.initialReduxState, this.appContext.locale)
        : props.initialReduxState,
    );

    setI18nLocale(this.appContext.locale.slice(0, 2));

    // First time on the render it comes from the props, then it's there and won't have to be refeched
    this.layoutData = props.layoutData || this.layoutData;
  }

  componentDidMount = () => {
    handleErrors();

    // Sync the state with the local storage
    this.reduxStore.subscribe(
      throttle(() => {
        const state = this.reduxStore.getState();
        const persistedStore = {
          tripInformation: { kids: getKids(state) },
          seller: state.seller,
        };
        setInLocalStorage(`${LOCAL_STORAGE_STATE_KEY}-${this.appContext.locale}`, persistedStore);
      }, 1000),
    );
  };

  render() {
    const { Component, pageProps, i18nData, disableFooter, disableHeader } = this.props;

    const pageContext: PageContext = {
      ...this.props.pageContext,
      ...(pageProps?.pageContext ?? {}),
    };

    const pageId = pageContext.pageId || 'pageWrapper';
    const isMicePage = b2cPagesService.isMicePage(pageContext.pageType);

    // Storing configuration from CMS in the context
    const appContext = {
      ...this.appContext,
      freeDepositDeadline:
        i18nData[
          this.appContext.isSeller
            ? 'booking.sellers.banking.freeDepositDeadline'
            : 'booking.banking.freeDepositDeadline'
        ],
    };

    if (typeof window !== 'undefined') {
      addTranslationKeys(i18nData);
    }

    return (
      <ErrorBoundary>
        <AppProviders
          appContext={appContext}
          pageContext={pageContext}
          reduxStore={this.reduxStore}
          toastRef={this.toastRef}
        >
          <HeadInfos />
          <PriorityThirdPartyScripts />
          <RouteChangeListener />
          <ThreeSixtyContainer />
          <InitializeSellerInfos appContext={this.appContext} />
          <Loading />
          {/* ContentSquare must ignore this id see https://docs.contentsquare.com/uxa-fr/#ignorer-remplacer-ou-ajouter-un-id */}
          <div id={pageId} data-cs-override-id="page">
            <PageLayout
              {...(this.layoutData as unknown as React.ComponentProps<typeof Layout>)}
              disableHeader={disableHeader}
              disableFooter={disableFooter}
              isMicePage={isMicePage}
            >
              {pageProps?.statusCode === 404 ? (
                <NotFoundView {...(pageProps as React.ComponentProps<typeof NotFoundView>)} />
              ) : (
                <Component {...(pageProps as Record<string, unknown>)} />
              )}
            </PageLayout>
          </div>
          <ToastContainer ref={this.toastRef} />
          <ThirdPartyScripts />
        </AppProviders>
      </ErrorBoundary>
    );
  }
}

export default App;
